<template>
  <v-card class="assets-table">
    <v-card-title class="toolbar primary white--text">
      <v-icon color="white">
        business
      </v-icon>
      <h2 class="ml-2">
        Vault Assets
      </h2>
      <v-spacer />
      <v-text-field
        v-model="search"
        primary
        dark
        solo-inverted
        flat
        append-icon="search"
        label="Filter results"
        single-line
        hide-details
      />
      <v-btn
        class="v-btn-fab"
        absolute
        dark
        fab
        bottom
        right
        color="warning"
        @click="createModalOpen = true"
      >
        <v-icon>add</v-icon>
      </v-btn>
    </v-card-title>

    <v-data-table
      disable-initial-sort
      item-key="token"
      class="elevation-1"
      :pagination.sync="pagination"
      :rows-per-page-items="getRangeItemsPerPage()"
      :headers="headers"
      :items="filteredResults"
      :loading="loading"
      :expand="expand"
      @update:pagination="updateQuery"
    >
      <template v-slot:items="props">
        <tr>
          <td class="text-xs-left">
            {{ props.item.name }}
          </td>
          <td class="text-xs-left">
            {{ props.item.project }}
          </td>
          <td class="text-xs-left">
            <!-- open wallet details -->
            <v-chip
              small
              outline
            >
              {{ props.item.token }}
              <v-tooltip
                top
                dark
              >
                <template v-slot:activator="{ on }">
                  <span
                    style="cursor:pointer"
                    v-on="on"
                    @click.prevent="() => $router.push(`/vault/wallets?search=${props.item.token}`)"
                  >
                    <v-icon
                      small
                      class="ml-2"
                    >
                      account_balance_wallet
                    </v-icon>
                  </span>
                </template>
                <span>Open {{ props.item.name }} in vault wallets section</span>
              </v-tooltip>
            </v-chip>
          </td>
          <td class="text-xs-left">
            {{ props.item.amount }}
          </td>
          <td class="text-xs-left">
            <v-icon :color="props.item.deleted ? 'error' : 'grey'">
              {{ props.item.deleted ? 'toggle_on' : 'toggle_off' }}
            </v-icon>
          </td>
          <!-- ACTIONS -->
          <td class="text-xs-left">
            <!-- load wallet -->
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  color="primary"
                  flat
                  icon
                  v-on="on"
                  @click="props.expanded = !props.expanded"
                >
                  <v-icon small>
                    visibility
                  </v-icon>
                </v-btn>
              </template>
              <span>{{ props.item.name }} distribution wallet details</span>
            </v-tooltip>
            <!-- edit -->
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  color="primary"
                  flat
                  icon
                  v-on="on"
                  @click="openEditAsset(props.item)"
                >
                  <v-icon small>
                    edit
                  </v-icon>
                </v-btn>
              </template>
              <span>edit</span>
            </v-tooltip>
            <!-- GENERATE REPORT BUTTON -->
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  color="primary"
                  flat
                  icon
                  v-on="on"
                  @click.stop="openReportAsset(props.item.name)"
                >
                  <v-icon>
                    drive_file_move_outline
                  </v-icon>
                </v-btn>
              </template>
              <span>Generate asset holders report and send to email</span>
            </v-tooltip>
            <!-- GENERATE REPORT BUTTON -->
          </td>
        </tr>
      </template>
      <!-- WALLET DETAILS EXPANDABLE CONTENT -->
      <template v-slot:expand="props">
        <WalletDetailCard :id="props.item.token" />
      </template>
    </v-data-table>
    <v-dialog
      v-model="createModalOpen"
      width="1000"
      persistent
    >
      <v-card :loading="loading">
        <v-card-title
          class="headline success white--text"
          primary-title
        >
          Create Asset
        </v-card-title>
        <v-form
          ref="createAssetForm"
          @submit.prevent=""
        >
          <v-card-text>
            <v-layout>
              <v-flex xs12>
                <v-text-field
                  v-model="formData.token"
                  label="Token Contract address"
                  :error-messages="getFieldErrors('token')"
                  :placeholder="'0x7eA90Acf7B0ED1011213FdC2827FC679446a40Be'"
                  @input="$v.formData.token.$touch()"
                  @blur="$v.formData.token.$touch()"
                />
                <v-text-field
                  v-model="formData.name"
                  label="Token symbol (Token name)"
                  :error-messages="getFieldErrors('name')"
                  :placeholder="'ETT1'"
                  @input="$v.formData.name.$touch()"
                  @blur="$v.formData.name.$touch()"
                />
                <v-text-field
                  v-model="formData.project"
                  label="Project"
                  :error-messages="getFieldErrors('project')"
                  :placeholder="'Berlin Test Token'"
                  @input="$v.formData.project.$touch()"
                  @blur="$v.formData.project.$touch()"
                />
                <v-text-field
                  v-model.number="formData.amount"
                  label="Amount"
                  type="number"
                  :error-messages="getFieldErrors('amount')"
                  :placeholder="'12345'"
                  @input="$v.formData.amount.$touch()"
                  @blur="$v.formData.amount.$touch()"
                />
              </v-flex>
            </v-layout>
            <v-flex
              v-if="vaultErrorOnAssetCreated"
              xs12
              class="error--text"
            >
              {{ vaultErrorOnAssetCreated }}
            </v-flex>
          </v-card-text>
          <v-divider />

          <v-card-actions>
            <v-spacer />
            <v-btn
              flat
              @click="createModalOpen = false"
            >
              Close
            </v-btn>
            <v-btn
              flat
              :disabled="$v.formData.$invalid || isLoading"
              :loading="isLoading"
              @click="handleSubmitCreateAsset"
            >
              Create
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="editModalOpen"
      width="1000"
      persistent
    >
      <v-card :loading="loading">
        <v-card-title
          class="headline success white--text"
          primary-title
        >
          <v-progress-linear
            v-if="loading"
            :indeterminate="true"
          />
          Update Asset
        </v-card-title>
        <v-form
          ref="editAssetForm"
          @submit.prevent=""
        >
          <v-card-text>
            <v-layout>
              <v-flex xs12>
                <v-text-field
                  v-model="formEditData.token"
                  label="Token Contract address"
                  disabled
                />
                <v-text-field
                  v-model="formEditData.name"
                  label="Token symbol (Token name)"
                  disabled
                />
                <v-text-field
                  v-model="formEditData.project"
                  label="Project"
                  :error-messages="getFieldErrors('project')"
                  @input="$v.formEditData.project.$touch()"
                  @blur="$v.formEditData.project.$touch()"
                />
                <v-text-field
                  v-model.number="formEditData.amount"
                  label="Amount"
                  type="number"
                  :error-messages="getFieldErrors('amount')"
                  @input="$v.formEditData.amount.$touch()"
                  @blur="$v.formEditData.amount.$touch()"
                />
              </v-flex>
            </v-layout>
            <v-flex
              v-if="vaultErrorOnAssetUpdated"
              xs12
              class="error--text"
            >
              {{ vaultErrorOnAssetUpdated }}
            </v-flex>
          </v-card-text>
          <v-divider />

          <v-card-actions>
            <v-btn
              flat
              @click="editModalOpen = false"
            >
              Close
            </v-btn>
            <v-spacer />
            <v-btn
              class="error mr-4"
              :disabled="loading"
              @click="handleSubmitDeleteAsset"
            >
              Delete
            </v-btn>
            <v-btn
              class="primary"
              flat
              :disabled="$v.formEditData.$invalid || isLoading"
              :loading="isLoading"
              @click="handleSubmitUpdateAsset"
            >
              Update
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="generateReportsModalOpen"
      width="500"
      persistent
    >
      <v-card :loading="isLoading">
        <v-card-title
          class="headline success white--text"
          primary-title
        >
          Generate report
        </v-card-title>
        <v-form
          ref="formGenerateReportData"
          @submit.prevent=""
        >
          <v-card-text>
            <v-layout>
              <v-flex xs12>
                <v-text-field
                  v-model="formGenerateReportData.email"
                  label="Email"
                  :error-messages="getErrorsGenerateForm('email')"
                  @input="$v.formGenerateReportData.email.$touch()"
                />
                <v-text-field
                  v-model="formGenerateReportData.assetName"
                  label="Asset name"
                  disabled
                  @input="$v.formGenerateReportData.assetName.$touch()"
                />
                <v-text-field
                  v-model="formGenerateReportData.cutOffTimestamp"
                  label="Cut off timestamp"
                  :error-messages="getErrorsGenerateForm('cutOffTimestamp')"
                  @input="$v.formGenerateReportData.cutOffTimestamp.$touch()"
                />
                <v-date-picker
                  v-model="formGenerateReportData.cutOffTimestamp"
                  :landscape="true"
                  label="Cut off timestamp"
                  :error-messages="getErrorsGenerateForm('cutOffTimestamp')"
                  @input="$v.formGenerateReportData.cutOffTimestamp.$touch()"
                />
              </v-flex>
            </v-layout>
          </v-card-text>
          <v-divider />

          <v-card-actions>
            <v-btn
              flat
              @click="generateReportsModalOpen = false"
            >
              Close
            </v-btn>
            <v-spacer />
            <v-btn
              :class="{ primary: !$v.formGenerateReportData.$invalid && !isLoading }"
              flat
              :disabled="$v.formGenerateReportData.$invalid || isLoading"
              :loading="isLoading"
              @click="createHoldersReport"
            >
              Generate
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import _ from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import { setQuery } from '@/helpers';
import WalletDetailCard from '@/components/shared/vault/WalletDetailCard';

export default {
  name: 'AssetsTable',
  components: {
    WalletDetailCard,
  },
  props: {
    assets: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      wallet: null,
      expand: false,
      isLoadTableData: false,
      createModalOpen: false,
      editModalOpen: false,
      generateReportsModalOpen: false,
      formData: {},
      formEditData: {},
      formGenerateReportData: {},
      search: undefined,
      isLoading: false,
      pagination: {
        page: 1,
        rowsPerPage: -1,
        sortBy: '',
        descending: false,
        totalItems: 0,
      },
      headers: [
        {
          text: 'Name',
          align: 'left',
          value: 'name',
        },
        {
          text: 'Project',
          align: 'left',
          value: 'project',
        },
        {
          text: 'Token',
          align: 'left',
          value: 'token',
        },
        {
          text: 'Amount',
          align: 'left',
          value: 'amount',
        },
        {
          text: 'Deleted',
          align: 'left',
          value: 'deleted',
        },
        {
          text: 'Actions',
          align: 'left',
          value: '',
          sortable: false,
        },
      ],
    };
  },
  computed: {
    ...mapGetters([
      'vaultAssetsTokens',
      'vaultSelectedAsset',
      'vaultErrorOnAssetCreated',
      'vaultErrorOnAssetUpdated',
      'getCurrentUser',
    ]),
    filteredResults() {
      if (this.search) {
        return this.assets.filter(asset => JSON.stringify(asset).toLowerCase().includes(this.search.toLowerCase()));
      }

      return this.assets;
    },
  },
  watch: {
    vaultSelectedAsset(asset) {
      this.formEditData = asset;
    },
    search: _.debounce(async function search(val) {
      this.updateQuery({
        ...this.$route.query,
        search: val,
      });
    }, 600),
  },
  validations() {
    return {
      formData: {
        token: { required },
        name: { required },
        project: { required },
        amount: { required },
      },
      formEditData: {
        token: { required },
        name: { required },
        project: { required },
        amount: { required },
      },
      formGenerateReportData: {
        email: { required },
        assetName: { required },
        cutOffTimestamp: { required },
      },
    };
  },
  beforeMount() {
    this.pagination = {
      sortBy: this.$route.query.sortBy || this.pagination.sortBy,
      rowsPerPage: parseInt(this.$route.query.limit, 0) || this.pagination.rowsPerPage,
      descending: this.$route.query.dir === 'desc' || this.pagination.descending,
      page: this.$route.query.page || this.pagination.page,
    };
    this.search = this.$route.query.search || null;
    this.formGenerateReportData.email = this.getCurrentUser.email;
  },
  methods: {
    ...mapActions([
      'getVaultAssets',
      'backPageAsset',
      'setLimitItemsPerPageAsset',
      'createVaultAsset',
      'updateVaultAsset',
      'setVaultSelectedAsset',
      'deleteVaultAsset',
      'generateHoldersReport',
    ]),
    async handleSubmitCreateAsset() {
      this.$v.$touch();

      if (!this.$v.formData.$invalid) {
        this.isLoading = true;
        try {
          await this.createVaultAsset(this.formData);
          this.createModalOpen = false;
          this.formData = {};
          this.$v.$reset();
        } catch (err) {
          console.info(err);
        }
        this.isLoading = false;
      }
    },
    async handleSubmitUpdateAsset() {
      this.$v.$touch();

      if (!this.$v.formEditData.$invalid) {
        this.isLoading = true;
        try {
          await this.updateVaultAsset(this.formEditData);
          this.editModalOpen = false;
          this.formEditData = {};
          this.$v.$reset();
        } catch (err) {
          console.info(err);
        }
        this.isLoading = false;
      }
    },
    async handleSubmitDeleteAsset() {
      this.deleteVaultAsset(this.formEditData.token);
      this.editModalOpen = false;
      this.formEditData = {};
    },
    openEditAsset(asset) {
      this.setVaultSelectedAsset(asset);
      this.editModalOpen = true;
    },
    openReportAsset(asset) {
      this.formGenerateReportData.assetName = asset;
      this.generateReportsModalOpen = true;
    },
    getFieldErrors(field) {
      const errors = [];
      if (this.$v.formData[field] || this.$v.formEditData[field]) {
        if ((!this.$v.formData[field].$dirty || !this.$v.formData[field].$invalid) ||
            (!this.$v.formEditData[field].$dirty || !this.$v.formEditData[field].$invalid)) {
          return errors;
        }

        if (!this.$v.formData[field].required || !this.$v.formEditData[field].required) {
          errors.push('Required field');
        }
      }

      return errors;
    },
    getErrorsGenerateForm(field) {
      const errors = [];
      if (this.$v.formGenerateReportData[field]) {
        if ((!this.$v.formGenerateReportData[field].$dirty || !this.$v.formGenerateReportData[field].$invalid)) {
          return errors;
        }

        if (!this.$v.formGenerateReportData[field].required) {
          errors.push('Required field');
        }
      }

      return errors;
    },

    updateQuery(pagination) {
      setQuery(
        {
          page: pagination.page,
          sortBy: pagination.sortBy,
          dir: pagination.descending ? 'desc' : 'asc',
          limit: pagination.rowsPerPage,
          search: this.search,
        },
        this.$route,
        this.$router,
      );
      this.$forceUpdate();
    },
    getRangeItemsPerPage() {
      const range = _.range(100, 201, 50);

      return [
        ...range.map(item => ({ text: item.toString(), value: item })),
        ({ text: 'All', value: -1 }),
      ];
    },
    async createHoldersReport() {
      if (!this.$v.formGenerateReportData.$invalid) {
        const selectedDate = this.$moment(new Date(this.formGenerateReportData.cutOffTimestamp).setHours(12, 0, 0, 0)).format('YYYY-MM-DD hh:mm:ss');

        const tzDate = this.$moment.tz(selectedDate, 'Europe/Berlin').valueOf();

        const payload = {
          email: this.formGenerateReportData.email,
          assetName: this.formGenerateReportData.assetName,
          cutOffTimestamp: tzDate,
        };

        try {
          this.isLoading = true;
          await this.generateHoldersReport(payload);
          this.generateReportsModalOpen = false;
        } catch (error) {
          console.error(error);
        }
        this.isLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>

.assets-table{
  .toolbar {
    position: relative;
    background-color: #1a4c7d;
    color: white;
    padding: 24px 24px 16px 24px;

    .v-btn-fab {
      right: 10px;
      bottom: -10px;
    }
  }
  .v-card__title {
    justify-content: flex-start;
    padding: 24px;
  }

  .expanded-details {
    background-color: #eaeaea;
    padding: 20px;
  }

  .v-card__title.subheading{
    .v-icon{
      margin-right:10px;
    }
  }
  .toolbar {
    padding: 24px 76px 34px 24px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/vault/VaultAssetsTable.vue